import { LoginOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { FreightRender } from '@components/OrderWizard/Panes/DisplayPane/DisplayComponents/SupplyOptions/FreightRenderer'
import { TotalPriceDisplay } from '@components/OrderWizard/Panes/DisplayPane/DisplayComponents/SupplyOptions/SupplyOptionCard'
import { BBDTag, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'

import { PopconfirmButton } from '@components/_SiteWide/PopconfirmButton'
import { SupplyOptionSort, useSupplyOptionContext } from '../../../../Contexts/SupplyOptionContext'
import { useWizardMaster } from '../../../../Contexts/WizardMasterContext'
import { formatLocationsList } from './util'
import { isDefined } from '@/utils/general'
import { CSSProperties } from 'react'

type MultipleOptionSummaryProps = {
  supplyOption: Gravitate.SupplyOption
  selected: boolean
  isBest: boolean
  originalDestinations?: string[]
  showLockIn?: boolean
  isCarrier?: boolean
}

export function MultipleOptionSummary({
  supplyOption,
  selected,
  isBest,
  originalDestinations = [],
  showLockIn,
  isCarrier,
}: MultipleOptionSummaryProps) {
  const { scopes } = useSecurityContext()
  const { selectedSort, setSelectedSupply, setNonOptimalChosen, setSelectedStatus, selectedStatus } =
    useSupplyOptionContext()
  const { handleUpdateSupplyOptions } = useWizardMaster()

  const lockInSupply = () => {
    handleUpdateSupplyOptions(setSelectedSupply, supplyOption, isBest)
    const notOptimal = selectedSort === 'Shortest' || !isBest
    notOptimal && setNonOptimalChosen(true)
    if (!selected) setSelectedStatus('selected')
  }

  const { formatDistance, formatCurrency } = useFormatter()
  const formattedDistance = formatDistance(null, true)?.toLowerCase()
  const formattedCurrency = formatCurrency(null, true)
  const showExceedsDirectiveAlert = supplyOption?.prices?.some((price) =>
    price.components?.some((component) => component.directive_info?.alert)
  )
  const showOutageAlert = supplyOption?.outage_info?.alert === 'error'

  return (
    <Vertical
      height={'auto'}
      verticalCenter
      className='p-4'
      style={{ maxWidth: 350, borderLeft: '1px solid var(--gray-300)' }}
    >
      <Vertical gap={12} flex={'none'} height={'auto'}>
        <Horizontal justifyContent='space-between' alignItems='center'>
          <Horizontal style={{ flexWrap: 'wrap' }} alignItems='center' gap={8}>
            <BestSupplyOptionTag
              isBest={isBest}
              selected={selected}
              selectedSort={selectedSort}
              isDefault={supplyOption.default_supply}
            />
            {supplyOption.out_of_network && (
              <BBDTag style={{ marginRight: 0 }} warning>
                <Texto category='p2' weight={'bold'} style={{ color: 'var(--theme-warning)' }}>
                  Out of Network
                </Texto>
              </BBDTag>
            )}
          </Horizontal>
          <Texto category='h5' weight={'bold'} style={{ color: 'var(--theme-color-3)' }}>
            <LoginOutlined /> {supplyOption.stops} stops
          </Texto>
        </Horizontal>

        <Horizontal verticalCenter justifyContent='space-between'>
          <SupplyOptionDataItem
            label={`Loaded ${formattedDistance}`}
            value={supplyOption.loaded_miles && Math.round(supplyOption.loaded_miles)}
          />
          {!isCarrier && scopes.price_visibility && (
            <SupplyOptionDataItem
              label='Freight'
              value={
                supplyOption.freight ? (
                  <FreightRender
                    supplyOption={supplyOption}
                    formattedCurrency={formattedCurrency}
                    formattedDistance={formattedDistance}
                  />
                ) : (
                  'N/A'
                )
              }
              align='right'
            />
          )}
        </Horizontal>
        {supplyOption?.unloaded_rate_per_gal ? (
          <Horizontal verticalCenter justifyContent='space-between'>
            <SupplyOptionDataItem
              label={`Unloaded ${formattedDistance}`}
              value={supplyOption.unloaded_miles && Math.round(supplyOption.unloaded_miles)}
            />
            {!isCarrier && scopes.price_visibility && (
              <SupplyOptionDataItem
                label='Freight'
                value={`${formattedCurrency}${supplyOption.unloaded_rate_per_gal.toFixed(5)}`}
                align='right'
              />
            )}
          </Horizontal>
        ) : null}
        <Vertical
          className='pt-2'
          style={{ borderTop: '1px solid var(--gray-300)' }}
          gap={'1em'}
          height={'auto'}
          flex={'none'}
        >
          {!isCarrier && <TotalPriceDisplay supplyOption={supplyOption} showTotalPrice={scopes?.price_visibility} />}

          {showLockIn && (
            <PopconfirmButton
              onConfirm={lockInSupply}
              buttonProps={{
                theme2: true,
                buttonText: selected ? 'KEEP SELECTED' : 'SELECT',
                className: 'option-select-button',
                'data-cy': 'lock-in-supply-btn',
                disabled: selected && selectedStatus === 'invalid',
              }}
              popconfirmProps={{
                disabled: !showOutageAlert && !showExceedsDirectiveAlert && !supplyOption.creates_detour,
                title: (
                  <PopconfirmTitles
                    showOutageAlert={showOutageAlert}
                    showExceedsDirectiveAlert={showExceedsDirectiveAlert}
                    supplyOption={supplyOption}
                    originalDestinations={originalDestinations}
                  />
                ),
                okText: 'Confirm',
                cancelText: 'Cancel',
                placement: 'topRight',
              }}
            />
          )}
        </Vertical>
      </Vertical>
    </Vertical>
  )
}

type BestSupplyOptionTagProps = {
  isBest: boolean
  selected: boolean
  selectedSort: SupplyOptionSort
  isDefault: boolean
}

function BestSupplyOptionTag({ isBest, selected, selectedSort, isDefault }: BestSupplyOptionTagProps) {
  const text = selected ? 'Currently Selected' : isDefault ? 'Default Supply' : isBest ? selectedSort + ' Option' : null

  if (text) {
    return (
      <BBDTag style={{ marginRight: 0 }} theme2>
        <Texto category='p2' weight={'bold'} style={{ color: 'var(--theme-color-2)' }}>
          {text}
        </Texto>
      </BBDTag>
    )
  }

  return null
}

type PopconfirmTitlesProps = {
  showOutageAlert: boolean
  showExceedsDirectiveAlert: boolean
  supplyOption: Gravitate.SupplyOption
  originalDestinations: string[]
}

function PopconfirmTitles({
  showOutageAlert,
  showExceedsDirectiveAlert,
  supplyOption,
  originalDestinations,
}: PopconfirmTitlesProps) {
  return (
    <Vertical gap={12}>
      {showOutageAlert && (
        <Vertical>
          <Texto weight='bold'>WARNING:</Texto>
          <Texto>
            This option has an outage at the planned load time. Are you sure you want to lock in this supply option?
          </Texto>
        </Vertical>
      )}
      {showExceedsDirectiveAlert && (
        <Vertical>
          <Texto weight='bold'>WARNING:</Texto>
          <Texto>
            This option's directive has exceeded its max. Are you sure you want to lock in this supply option?
          </Texto>
        </Vertical>
      )}
      {supplyOption.creates_detour && (
        <Vertical>
          <Texto weight='bold'>NO LOAD CONFIRMATION:</Texto>
          <Texto>
            Driver has already arrived at <b>{formatLocationsList(originalDestinations, 'or')}</b>.
          </Texto>
          <Texto>
            Updating to{' '}
            <b>
              {formatLocationsList(
                supplyOption.payload.loads.map((load) => load.location_name),
                'and'
              )}
            </b>{' '}
            will result in a <b>No Load</b>.
          </Texto>
        </Vertical>
      )}
    </Vertical>
  )
}

type SupplyOptionDataItemProps = {
  label: string
  value: React.ReactNode
  align?: CSSProperties['textAlign']
}

function SupplyOptionDataItem({ label, value, align = 'left' }: SupplyOptionDataItemProps) {
  if (!value) return null
  return (
    <Vertical style={{ flex: 'none' }} justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}>
      <Texto style={{ textAlign: align, color: 'var(--gray-500)' }} category='label'>
        {label.toUpperCase()}
      </Texto>
      <Texto style={{ textAlign: align }} category='p2' weight={'bold'}>
        {value}
      </Texto>
    </Vertical>
  )
}
